function envActive(key) {
  return process.env[key] === 'true';
}

const CARD_PROVIDER_RR_KEY = 'checkout_card_provider_rr';

/**
 * Card gateway IDs available for the current checkout mode.
 * Provider names are intentionally not exposed to the UI.
 */
export function getCardProviders({ isSubscription, currency }) {
  const currencyCode = currency || 'BRL';
  const providers = [];

  if (isSubscription) {
    if (envActive('VUE_APP_IUGU_ACTIVE') && currencyCode === 'BRL') {
      providers.push('iugu-subscription');
    }
    if (envActive('VUE_APP_PAYPAL_ACTIVE')) {
      providers.push('paypal-subscription');
    }
    if (envActive('VUE_APP_GALAXPAY_ACTIVE') && currencyCode === 'BRL') {
      providers.push('galaxpay-subscription');
    }
  } else {
    if (envActive('VUE_APP_IUGU_ACTIVE') && currencyCode === 'BRL') {
      providers.push('card-iugu');
    }
    if (envActive('VUE_APP_PAYPAL_ACTIVE')) {
      providers.push('card');
    }
    if (envActive('VUE_APP_GALAXPAY_ACTIVE') && currencyCode === 'BRL') {
      providers.push('card-galaxpay');
    }
  }

  return providers;
}

const IUGU_CARD_IDS = ['card-iugu', 'iugu-subscription'];
const PAYPAL_CARD_IDS = ['card', 'paypal-subscription'];

export function isIuguCardProvider(method) {
  return IUGU_CARD_IDS.indexOf(method) !== -1;
}

export function isPayPalCardProvider(method) {
  return PAYPAL_CARD_IDS.indexOf(method) !== -1;
}

/** PayPal Plus (one-time): iframe na mesma página, sem redirect externo. */
export function isEmbeddedPayPalCheckout(method) {
  return method === 'card';
}

export function isExternalCardCheckout(method) {
  return isIuguCardProvider(method) || method === 'paypal-subscription';
}

export function pickRandomProvider(providers, exclude = []) {
  const available = (providers || []).filter(id => exclude.indexOf(id) === -1);
  if (!available.length) {
    return null;
  }
  const index = Math.floor(Math.random() * available.length);
  return available[index];
}

/** Alterna Iugu/PayPal por sessão do browser; aleatório só quando há 1 par ou fallback. */
export function pickBalancedCardProvider(providers, exclude = []) {
  const available = (providers || []).filter(id => exclude.indexOf(id) === -1);
  if (!available.length) {
    return null;
  }

  const iugu = available.find(id => isIuguCardProvider(id));
  const paypal = available.find(id => isPayPalCardProvider(id));

  if (iugu && paypal) {
    let last = null;
    try {
      last = sessionStorage.getItem(CARD_PROVIDER_RR_KEY);
    } catch (e) {
      last = null;
    }

    const picked = last === iugu ? paypal : iugu;
    try {
      sessionStorage.setItem(CARD_PROVIDER_RR_KEY, picked);
    } catch (e) {
      // ignore quota / private mode
    }

    return picked;
  }

  return pickRandomProvider(available, exclude);
}

export function peekCardProviderBalance({ isSubscription, currency, exclude = [] }) {
  const providers = getCardProviders({ isSubscription, currency });
  const available = providers.filter(id => exclude.indexOf(id) === -1);
  return {
    providers,
    available,
    iugu: available.find(id => isIuguCardProvider(id)) || null,
    paypal: available.find(id => isPayPalCardProvider(id)) || null,
    paypalActive: envActive('VUE_APP_PAYPAL_ACTIVE'),
    iuguActive: envActive('VUE_APP_IUGU_ACTIVE')
  };
}

export function getPaymentTypes({ currency, isSubscription }) {
  const currencyCode = currency || 'BRL';
  const types = [];
  const hasCard = getCardProviders({ isSubscription, currency: currencyCode }).length > 0;

  if (hasCard) {
    types.push({ id: 'card', label: 'Cartão de Crédito' });
  }

  // PIX / boleto only for one-time BRL checkouts
  if (!isSubscription && currencyCode === 'BRL') {
    if (envActive('VUE_APP_STARKBANK_ACTIVE')) {
      types.push({ id: 'pix', label: 'PIX' });
    }
    if (envActive('VUE_APP_BOLETO_ACTIVE')) {
      types.push({ id: 'boleto', label: 'Boleto' });
    }
  }

  return types;
}

export function resolvePaymentMethod(typeId, { isSubscription, currency, exclude = [] }) {
  if (typeId === 'pix') {
    return 'pix';
  }
  if (typeId === 'boleto') {
    return 'boleto';
  }
  if (typeId === 'card') {
    const providers = getCardProviders({ isSubscription, currency });
    const method = pickBalancedCardProvider(providers, exclude);
    if (isSubscription && method === 'card') {
      return pickBalancedCardProvider(providers.filter(id => id !== 'card'), exclude);
    }
    return method;
  }
  return null;
}

export const KNOWN_PAYMENT_METHODS = [
  'card-stripe',
  'card',
  'paypal-order',
  'stripe-subscription',
  'card-pagarme',
  'pagarme-subscription',
  'card-pagseguro',
  'pagseguro-subscription',
  'card-mercadopago',
  'mercadopago-subscription',
  'card-iugu',
  'iugu-subscription',
  'card-galaxpay',
  'galaxpay-subscription',
  'paypal-subscription',
  'boleto',
  'transferencia',
  'pix',
  'picpay'
];

export function isKnownPaymentMethod(method) {
  return KNOWN_PAYMENT_METHODS.indexOf(method) !== -1;
}
